import { Component, EventEmitter, inject, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { cache } from '@common/util/cache.operator';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { LicenseIdpLogoutMode } from '@portal-core/licenses/enums/license-idp-logout-mode.enum';
import { LicenseSamlAuthenticationSettings } from '@portal-core/licenses/models/license-saml-authentication-settings.model';
import { License } from '@portal-core/licenses/models/license.model';
import { LicensesService } from '@portal-core/licenses/services/licenses.service';
import { Team } from '@portal-core/teams/models/team.model';
import { TeamsService } from '@portal-core/teams/services/teams.service';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { LoadingState } from '@portal-core/util/loading-state';
import { BehaviorSubject, Observable, Subscription, map, of, switchMap, tap } from 'rxjs';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { TeamAvatarComponent } from '@portal-core/teams/components/team-avatar/team-avatar.component';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { McColorDirective } from '@portal-core/ui/themes/directives/mc-color.directive';
import { MatInput } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { FormControlErrorMessageComponent } from '@portal-core/forms/components/form-control-error-message/form-control-error-message.component';
import { AutocompleteInputComponent } from '@portal-core/ui/autocomplete/components/autocomplete-input/autocomplete-input.component';
import { TeamsSelectListComponent } from '@portal-core/teams/components/teams-select-list/teams-select-list.component';
import { AutocompleteItemDirective } from '@portal-core/ui/autocomplete/directives/autocomplete-item/autocomplete-item.directive';
import { ErrorsExpansionPanelComponent } from '@portal-core/general/components/errors-expansion-panel/errors-expansion-panel.component';
import { LoaderComponent } from '@portal-core/general/components/loader/loader.component';

@Component({
    selector: 'mc-license-sso-form',
    templateUrl: './license-sso-form.component.html',
    styleUrls: ['./license-sso-form.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [NgIf, MatIcon, NgFor, TeamAvatarComponent, MatButton, ReactiveFormsModule, MatCheckbox, MatFormField, McColorDirective, MatLabel, MatInput, MatError, MatRadioGroup, MatRadioButton, FormControlErrorMessageComponent, AutocompleteInputComponent, TeamsSelectListComponent, AutocompleteItemDirective, ErrorsExpansionPanelComponent, LoaderComponent, AsyncPipe]
})
@AutoUnsubscribe()
export class LicenseSsoFormComponent implements OnInit {
  private readonly licensesService = inject(LicensesService);
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly errorService = inject(ErrorService);
  private readonly teamsService = inject(TeamsService);
  @Input() license: License;
  @Output() closeDialog: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() saved: EventEmitter<void> = new EventEmitter<void>();
  @InputObservable('license') license$: Observable<License>;

  IdpLogoutMode: typeof LicenseIdpLogoutMode = LicenseIdpLogoutMode;

  createViewerUserOnDemandValueChangesSubscription: Subscription;
  enabledValueChangesSubscription: Subscription;
  idpLogoutEndpointValueChangesSubscription: Subscription;
  isEditing: boolean;
  isSamlAuthenticationConfigured: boolean;
  ssoSettings: LicenseSamlAuthenticationSettings;
  ssoSettings$: Observable<LicenseSamlAuthenticationSettings>;
  ssoSettingsForm: UntypedFormGroup;
  loadingState: LoadingState<string> = new LoadingState<string>();
  reload$: BehaviorSubject<void> = new BehaviorSubject(undefined);
  samlAuthenticationSettings$: Observable<LicenseSamlAuthenticationSettings>;
  savingState: LoadingState<string> = new LoadingState<string>();

  constructor() {
    this.buildForm();
  }

  ngOnInit() {
    this.ssoSettings$ = this.reload$.pipe(
      tap(() => this.loadingState.update(true)),
      switchMap(() => this.license$),
      switchMap<License, Observable<LicenseSamlAuthenticationSettings>>(license => {
        if (license) {
          return this.licensesService.getSamlAuthenticationSettingsAndDefaultTeams$(license.Id);
        } else {
          return of(null);
        }
      }),
      // Load the team data for the default teams avatar list
      switchMap(ssoSettings => {
        return this.teamsService.loadItems$(ssoSettings?.SSODefaultTeamIds).pipe(
          map(() => ssoSettings)
        );
      }),
      cache()
    );

    this.ssoSettings$.subscribe(ssoSettings => {
      this.ssoSettings = ssoSettings;
      this.isSamlAuthenticationConfigured = !!ssoSettings;
      this.resetForm(ssoSettings ?? {} as LicenseSamlAuthenticationSettings);
      this.loadingState.update(false);
    }, error => {
      this.loadingState.update(false, 'Unable to get the single sign-on settings.', this.errorService.getErrorMessages(error));
    });
  }

  onConfigureSamlAuthentication() {
    // Reset the form when switching to edit mode. This makes sure the form shows the current settings even if the user changed the form and canceled out later
    this.resetForm(this.ssoSettings ?? {} as LicenseSamlAuthenticationSettings);
    this.isEditing = true;
  }

  onSubmit() {
    if (!this.ssoSettingsForm.valid) {
      return;
    }

    this.savingState.update(true);

    const ssoSettings: LicenseSamlAuthenticationSettings = {
      Enabled: this.ssoSettingsForm.get('saml.enabled').value ?? false,
      IdentityProviderIssuer: this.ssoSettingsForm.get('saml.identityProviderIssuer').value,
      IdpLogout: this.getIdpLogoutValue(this.ssoSettingsForm.get('singleLogout.idpLogout').value),
      IdpLogoutEndpoint: this.ssoSettingsForm.get('saml.idpLogoutEndpoint').value,
      PublicCertificate: this.ssoSettingsForm.get('saml.publicCertificate').value,
      SamlEndpoint: this.ssoSettingsForm.get('saml.samlEndpoint').value,
      LicenseId: this.license.Id,
      SignInButtonLabel: this.ssoSettingsForm.get('customization.signInButtonLabel').value,
      CreateViewerUserOnDemand: this.ssoSettingsForm.get('settings.createViewerUserOnDemand').value ?? false,
      SSODefaultTeamIds: this.ssoSettingsForm.get('settings.teams').value
    };

    this.licensesService.setSamlAuthenticationSettings$(this.license.Id, ssoSettings).subscribe(() => {
      this.reload$.next();
      this.savingState.update(false);
      this.isEditing = false;
      this.saved.emit();
    }, error => {
      this.savingState.update(false, 'Unable to update the license SAML authentication settings.', this.errorService.getErrorMessages(error));
    });
  }

  getTeamById$(teamId: number): Observable<Team> {
    return this.teamsService.getItemById$(teamId, { allowApiRequest: false });
  }

  /** The read-only description of the license's single logout setting */
  getIdpLogoutLabel(idpLogout: boolean | null): string {
    if (idpLogout == null) {
      return 'Let each user decide';
    }

    return idpLogout ? 'Always' : 'Never';
  }

  /** Converts the license's single logout setting into the radio group's value */
  private getIdpLogoutMode(idpLogout: boolean | null): LicenseIdpLogoutMode {
    if (idpLogout == null) {
      return LicenseIdpLogoutMode.User;
    }

    return idpLogout ? LicenseIdpLogoutMode.Always : LicenseIdpLogoutMode.Never;
  }

  /** Converts the radio group's value into the license's single logout setting. Null means each user decides. */
  private getIdpLogoutValue(mode: LicenseIdpLogoutMode): boolean | null {
    switch (mode) {
      case LicenseIdpLogoutMode.Always:
        return true;
      case LicenseIdpLogoutMode.Never:
        return false;
      default:
        return null;
    }
  }

  private buildForm() {
    this.ssoSettingsForm = this.formBuilder.group({
      saml: new UntypedFormGroup({
        enabled: new UntypedFormControl(false),
        samlEndpoint: new UntypedFormControl(null),
        idpLogoutEndpoint: new UntypedFormControl(null),
        identityProviderIssuer: new UntypedFormControl(null),
        publicCertificate: new UntypedFormControl(null)
      }),
      settings: new UntypedFormGroup({
        createViewerUserOnDemand: new UntypedFormControl(false),
        teams: new UntypedFormControl(null, Validators.required)
      }),
      singleLogout: new UntypedFormGroup({
        idpLogout: new UntypedFormControl(LicenseIdpLogoutMode.User)
      }),
      customization: new UntypedFormGroup({
        signInButtonLabel: new UntypedFormControl(null)
      })
    });

    // Observe the createViewerUserOnDemand value changing to update the state of the teams control
    this.createViewerUserOnDemandValueChangesSubscription = this.ssoSettingsForm.get('settings.createViewerUserOnDemand').valueChanges.subscribe(createViewerUserOnDemand => {
      const teamsControl = this.ssoSettingsForm.get('settings.teams');

      if (createViewerUserOnDemand) {
        teamsControl.enable();
      } else {
        teamsControl.setValue(null);
        teamsControl.disable();
      }
    });

    // Observe the enabled value changing to update the state of the saml controls
    this.enabledValueChangesSubscription = this.ssoSettingsForm.get('saml.enabled').valueChanges.subscribe(enabled => {
      if (enabled) {
        this.ssoSettingsForm.get('saml.samlEndpoint').setValidators([Validators.required]);
        this.ssoSettingsForm.get('saml.identityProviderIssuer').setValidators([Validators.required]);
        this.ssoSettingsForm.get('saml.publicCertificate').setValidators([Validators.required]);
      } else {
        this.ssoSettingsForm.get('saml.samlEndpoint').setValidators(null);
        this.ssoSettingsForm.get('saml.identityProviderIssuer').setValidators(null);
        this.ssoSettingsForm.get('saml.publicCertificate').setValidators(null);
      }

      this.ssoSettingsForm.get('saml').updateValueAndValidity();
    });

    // Observe the SLO endpoint value changing to update the state of the idpLogout control
    this.idpLogoutEndpointValueChangesSubscription = this.ssoSettingsForm.get('saml.idpLogoutEndpoint').valueChanges.subscribe(idpLogoutEndpoint => {
      this.updateIdpLogoutControl(idpLogoutEndpoint);
    });
  }

  /**
   * Only lets the license override single logout once there is an SLO endpoint to send the user to.
   * Falls back to letting each user decide when the endpoint is cleared, since a license override could not take effect anyway.
   */
  private updateIdpLogoutControl(idpLogoutEndpoint: string) {
    const idpLogoutControl = this.ssoSettingsForm.get('singleLogout.idpLogout');

    if (idpLogoutEndpoint?.trim()) {
      idpLogoutControl.enable();
    } else {
      idpLogoutControl.setValue(LicenseIdpLogoutMode.User);
      idpLogoutControl.disable();
    }
  }

  private resetForm(ssoSettings: LicenseSamlAuthenticationSettings) {
    this.ssoSettingsForm.reset({
      saml: {
        enabled: ssoSettings.Enabled ?? false,
        identityProviderIssuer: ssoSettings.IdentityProviderIssuer ?? '',
        idpLogoutEndpoint: ssoSettings.IdpLogoutEndpoint ?? '',
        publicCertificate: ssoSettings.PublicCertificate ?? '',
        samlEndpoint: ssoSettings.SamlEndpoint ?? ''
      },
      settings: {
        createViewerUserOnDemand: ssoSettings.CreateViewerUserOnDemand ?? false,
        teams: ssoSettings.SSODefaultTeamIds ?? null
      },
      singleLogout: {
        idpLogout: this.getIdpLogoutMode(ssoSettings.IdpLogout)
      },
      customization: {
        signInButtonLabel: ssoSettings.SignInButtonLabel ?? ''
      }
    });

    // Update the teams control
    if (ssoSettings.CreateViewerUserOnDemand) {
      this.ssoSettingsForm.get('settings.teams').enable();
    } else {
      this.ssoSettingsForm.get('settings.teams').disable();
    }

    // Update the saml controls
    if (ssoSettings.Enabled) {
      this.ssoSettingsForm.get('saml.samlEndpoint').setValidators([Validators.required]);
      this.ssoSettingsForm.get('saml.identityProviderIssuer').setValidators([Validators.required]);
      this.ssoSettingsForm.get('saml.publicCertificate').setValidators([Validators.required]);
    } else {
      this.ssoSettingsForm.get('saml.samlEndpoint').setValidators(null);
      this.ssoSettingsForm.get('saml.identityProviderIssuer').setValidators(null);
      this.ssoSettingsForm.get('saml.publicCertificate').setValidators(null);
    }

    // Update the idpLogout control
    this.updateIdpLogoutControl(ssoSettings.IdpLogoutEndpoint);

    this.ssoSettingsForm.get('saml').updateValueAndValidity();
  }
}
